import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";

/**
 * Capitalizes and formats snake_case or camelCase keys into human-readable field labels.
 * E.g., 'origin_port_full_name' -> 'Origin Port Full Name'
 */
const formatFieldLabel = (key) => {
  if (!key) return "";

  // Mapping for known abbreviations or specific custom keys
  const acronyms = {
    id: "ID",
    admin_id: "Admin ID",
    created_by: "Created By",
    user_id: "User ID",
    organization_id: "Organization ID",
    branch_id: "Branch ID",
    incoterm_id: "Incoterm ID",
    commodity_id: "Commodity ID",
    requested_currency_id: "Requested Currency ID",
    fl_quantity: "Full Container Quantity",
    fl_weight: "Full Container Weight",
    pl_package_info: "Package Information",
    pincode: "Pincode",
    pincode_notavailable: "Pincode Not Available",
    ip_address: "IP Address",
    url: "URL",
    status: "Status",
    last_status_id: "Last Status ID",
    picked_user_id: "Picked User ID",
  };

  if (acronyms[key]) return acronyms[key];

  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to spaces
    .replace(/_/g, " ") // snake_case to spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

/**
 * Safely parses string values that might contain JSON (like stringified package arrays).
 */
const safeParse = (data) => {
  if (data === null || data === undefined) return null;
  if (typeof data === "object") return data;
  if (typeof data === "string") {
    const trimmed = data.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        return JSON.parse(trimmed);
      } catch (e) {
        return data;
      }
    }
  }
  return data;
};

/**
 * Renders nested objects, arrays, booleans, and values into clean HTML.
 */
const ValueRenderer = ({ value }) => {
  const parsed = safeParse(value);

  if (parsed === null || parsed === undefined || parsed === "") {
    return <span className="text-muted fst-italic">-</span>;
  }

  if (typeof parsed === "boolean") {
    return (
      <span className={`badge ${parsed ? "bg-success text-white" : "bg-secondary text-white"} px-2 py-1`}>
        {parsed ? "True" : "False"}
      </span>
    );
  }

  // Handle Array of Objects or Primitives
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return <span className="text-muted fst-italic">Empty List</span>;

    // Check if array items are objects (like package info)
    const isObjectList = typeof parsed[0] === "object" && parsed[0] !== null;

    if (isObjectList) {
      const headers = Object.keys(parsed[0]);
      return (
        <div className="table-responsive border rounded bg-white my-1" style={{ maxHeight: "200px" }}>
          <table className="table table-sm table-striped table-bordered mb-0 align-middle" style={{ fontSize: "11px" }}>
            <thead className="table-light">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="text-nowrap">{formatFieldLabel(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsed.map((item, idx) => (
                <tr key={idx}>
                  {headers.map((h) => (
                    <td key={h} className="text-nowrap">
                      {item[h] !== null && item[h] !== undefined ? String(item[h]) : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Primitive Array (e.g., [10, 9])
    return (
      <div className="d-flex flex-wrap gap-1 align-items-center">
        {parsed.map((item, idx) => (
          <span key={idx} className="badge bg-light text-dark border me-1 fw-medium">
            {String(item)}
          </span>
        ))}
      </div>
    );
  }

  // Handle single object
  if (typeof parsed === "object") {
    const keys = Object.keys(parsed);
    if (keys.length === 0) return <span className="text-muted fst-italic">Empty Object</span>;
    return (
      <div className="p-2 border rounded bg-white text-dark" style={{ fontSize: "12px" }}>
        {keys.map((k) => (
          <div key={k} className="d-flex justify-content-between border-bottom py-1">
            <span className="fw-semibold text-secondary me-2">{formatFieldLabel(k)}:</span>
            <span className="text-break"><ValueRenderer value={parsed[k]} /></span>
          </div>
        ))}
      </div>
    );
  }

  // Standard Primitive String or Number
  const strVal = String(parsed);

  // Check if string looks like an ISO date
  if (typeof parsed === "string" && /^\d{4}-\d{2}-\d{2}(T|\b)/.test(parsed)) {
    const d = new Date(parsed);
    if (!isNaN(d.getTime())) {
      return <span>{d.toLocaleString()}</span>;
    }
  }

  return <span className="text-break fw-normal">{strVal}</span>;
};

const AuditDataViewer = ({ oldData, newData, auditStatus, action }) => {
  const [viewMode, setViewMode] = useState("diff"); // 'diff' | 'formatted' | 'raw'
  const [showChangesOnly, setShowChangesOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isFailed = auditStatus && String(auditStatus).toUpperCase() === "FAILED";

  const parsedOld = useMemo(() => safeParse(oldData) || {}, [oldData]);
  const parsedNew = useMemo(() => safeParse(newData) || {}, [newData]);

  const hasOld = oldData !== null && oldData !== undefined && oldData !== "";
  const hasNew = newData !== null && newData !== undefined && newData !== "";

  // Combine and compare keys from both Old and New objects
  const diffList = useMemo(() => {
    const oldKeys = Object.keys(parsedOld);
    const newKeys = Object.keys(parsedNew);
    const allKeysSet = new Set([...oldKeys, ...newKeys]);

    const result = [];

    allKeysSet.forEach((key) => {
      const inOld = Object.prototype.hasOwnProperty.call(parsedOld, key);
      const inNew = Object.prototype.hasOwnProperty.call(parsedNew, key);

      const valOld = parsedOld[key];
      const valNew = parsedNew[key];

      let status = "UNCHANGED"; // 'MODIFIED' | 'ADDED' | 'REMOVED' | 'UNCHANGED'

      if (inOld && !inNew) {
        status = "REMOVED";
      } else if (!inOld && inNew) {
        status = "ADDED";
      } else {
        const normOld = JSON.stringify(safeParse(valOld));
        const normNew = JSON.stringify(safeParse(valNew));
        if (normOld !== normNew) {
          status = "MODIFIED";
        }
      }

      result.push({
        key,
        label: formatFieldLabel(key),
        valOld,
        valNew,
        status,
        isChanged: status !== "UNCHANGED",
      });
    });

    return result;
  }, [parsedOld, parsedNew]);

  // Filtered diff list based on search term & showChangesOnly
  const filteredDiff = useMemo(() => {
    return diffList.filter((item) => {
      if (showChangesOnly && !item.isChanged) return false;
      if (searchTerm.trim() === "") return true;

      const term = searchTerm.toLowerCase();
      const matchLabel = item.label.toLowerCase().includes(term);
      const matchKey = item.key.toLowerCase().includes(term);
      const matchOld = item.valOld !== undefined && String(item.valOld).toLowerCase().includes(term);
      const matchNew = item.valNew !== undefined && String(item.valNew).toLowerCase().includes(term);

      return matchLabel || matchKey || matchOld || matchNew;
    });
  }, [diffList, showChangesOnly, searchTerm]);

  const countChanged = useMemo(() => diffList.filter((d) => d.isChanged).length, [diffList]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  return (
    <div className="audit-data-viewer">
      {/* Operation Status Warning if FAILED */}
      {isFailed && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-3 py-2 px-3 border-danger shadow-sm">
          <div className="d-flex align-items-center gap-2">
            <i className="ti ti-alert-triangle-filled fs-4 text-danger"></i>
            <div>
              <strong>Operation Status: FAILED</strong>
              <div className="small text-secondary">
                This {action ? action.toLowerCase() : "action"} failed to complete. The added/changed data below shows what was attempted.
              </div>
            </div>
          </div>
          <span className="badge bg-danger text-white px-3 py-2 text-uppercase">FAILED</span>
        </div>
      )}

      {/* Top Header & Toolbar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom">
        <div className="btn-group btn-group-sm" role="group">
          <button
            type="button"
            className={`btn ${viewMode === "diff" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setViewMode("diff")}
          >
            <i className="ti ti-git-compare me-1"></i> Diff Comparison
            {countChanged > 0 && (
              <span className={`badge ${isFailed ? "bg-danger" : "bg-primary"} ms-1`}>
                {countChanged} {isFailed ? "added/changed (FAILED)" : "changed"}
              </span>
            )}
          </button>
          <button
            type="button"
            className={`btn ${viewMode === "formatted" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setViewMode("formatted")}
          >
            <i className="ti ti-layout-grid me-1"></i> Formatted Cards
          </button>
          <button
            type="button"
            className={`btn ${viewMode === "raw" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setViewMode("raw")}
          >
            <i className="ti ti-code me-1"></i> Raw JSON
          </button>
        </div>

        {/* Filter controls */}
        {viewMode !== "raw" && (
          <div className="d-flex align-items-center gap-3">
            {viewMode === "diff" && (
              <div className="form-check form-switch m-0 d-flex align-items-center">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id="showChangesOnly"
                  checked={showChangesOnly}
                  onChange={(e) => setShowChangesOnly(e.target.checked)}
                />
                <label className="form-check-label small fw-medium text-secondary" htmlFor="showChangesOnly">
                  Show Changes Only
                </label>
              </div>
            )}
            <div className="input-group input-group-sm" style={{ width: "200px" }}>
              <span className="input-group-text bg-white border-end-0">
                <i className="ti ti-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder="Search field..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* VIEW MODE 1: DIFF COMPARISON TABLE */}
      {viewMode === "diff" && (
        <div>
          {filteredDiff.length === 0 ? (
            <div className="alert alert-light border text-center my-3 py-4 text-muted">
              <i className="ti ti-file-search fs-3 d-block mb-1 text-secondary"></i>
              {showChangesOnly && countChanged === 0
                ? "No changes detected between Old Data and New Data."
                : "No matching fields found."}
            </div>
          ) : (
            <div className="table-responsive border rounded" style={{ maxHeight: "450px" }}>
              <table className="table table-hover table-bordered mb-0 align-middle" style={{ fontSize: "13px" }}>
                <thead className="table-light sticky-top shadow-sm" style={{ zIndex: 1 }}>
                  <tr>
                    <th style={{ width: "25%" }}>Field Name</th>
                    {hasOld && <th style={{ width: "30%" }}>Old Value</th>}
                    {hasNew && <th style={{ width: "30%" }}>New Value</th>}
                    <th className="text-center" style={{ width: "15%" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDiff.map((row) => {
                    let rowBgClass = "";
                    let badgeClass = "bg-light text-secondary border";
                    let badgeText = "UNCHANGED";

                    if (row.status === "MODIFIED") {
                      rowBgClass = isFailed ? "table-danger bg-danger-subtle" : "table-warning bg-warning-subtle";
                      badgeClass = isFailed ? "bg-danger text-white fw-bold" : "bg-warning text-dark fw-bold";
                      badgeText = isFailed ? "FAILED (MODIFIED)" : "MODIFIED";
                    } else if (row.status === "ADDED") {
                      rowBgClass = isFailed ? "table-danger bg-danger-subtle" : "table-success bg-success-subtle";
                      badgeClass = isFailed ? "bg-danger text-white fw-bold" : "bg-success text-white fw-bold";
                      badgeText = isFailed ? "FAILED (ADDED)" : "ADDED";
                    } else if (row.status === "REMOVED") {
                      rowBgClass = "table-danger bg-danger-subtle";
                      badgeClass = "bg-danger text-white fw-bold";
                      badgeText = isFailed ? "FAILED (REMOVED)" : "REMOVED";
                    }

                    return (
                      <tr key={row.key} className={rowBgClass}>
                        <td className="fw-semibold text-dark">
                          <div>{row.label}</div>
                          <small className="text-muted font-monospace" style={{ fontSize: "11px" }}>{row.key}</small>
                        </td>

                        {hasOld && (
                          <td className={row.status === "MODIFIED" ? "bg-danger-subtle text-dark" : ""}>
                            <ValueRenderer value={row.valOld} />
                          </td>
                        )}

                        {hasNew && (
                          <td className={row.status === "MODIFIED" ? "bg-success-subtle text-dark" : (row.status === "ADDED" && isFailed ? "bg-danger-subtle text-dark" : "")}>
                            <ValueRenderer value={row.valNew} />
                          </td>
                        )}

                        <td className="text-center">
                          <span className={`badge ${badgeClass} px-2 py-1`} style={{ fontSize: "10px" }}>
                            {badgeText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* VIEW MODE 2: FORMATTED CARDS */}
      {viewMode === "formatted" && (
        <div className="row g-3">
          {hasOld && (
            <div className={hasNew ? "col-md-6" : "col-12"}>
              <div className="card border shadow-sm h-100">
                <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
                  <h6 className="card-title mb-0 fw-bold text-danger">
                    <i className="ti ti-history me-1"></i> Old Data {isFailed && <span className="badge bg-danger ms-1">FAILED</span>}
                  </h6>
                  <button
                    className="btn btn-sm btn-link text-decoration-none p-0 text-secondary"
                    onClick={() => copyToClipboard(JSON.stringify(parsedOld, null, 2), "Old Data")}
                    title="Copy Old Data JSON"
                  >
                    <i className="ti ti-copy"></i> Copy
                  </button>
                </div>
                <div className="card-body p-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <div className="row g-2">
                    {Object.keys(parsedOld)
                      .filter((k) => !searchTerm || formatFieldLabel(k).toLowerCase().includes(searchTerm.toLowerCase()) || k.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((k) => (
                        <div key={k} className="col-12 border-bottom pb-2 mb-1">
                          <label className="text-muted small fw-semibold d-block mb-1">
                            {formatFieldLabel(k)}
                          </label>
                          <div><ValueRenderer value={parsedOld[k]} /></div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {hasNew && (
            <div className={hasOld ? "col-md-6" : "col-12"}>
              <div className="card border shadow-sm h-100">
                <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
                  <h6 className={`card-title mb-0 fw-bold ${isFailed ? "text-danger" : "text-success"}`}>
                    <i className="ti ti-file-text me-1"></i> New Data {isFailed && <span className="badge bg-danger ms-1">FAILED (ADDED)</span>}
                  </h6>
                  <button
                    className="btn btn-sm btn-link text-decoration-none p-0 text-secondary"
                    onClick={() => copyToClipboard(JSON.stringify(parsedNew, null, 2), "New Data")}
                    title="Copy New Data JSON"
                  >
                    <i className="ti ti-copy"></i> Copy
                  </button>
                </div>
                <div className="card-body p-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <div className="row g-2">
                    {Object.keys(parsedNew)
                      .filter((k) => !searchTerm || formatFieldLabel(k).toLowerCase().includes(searchTerm.toLowerCase()) || k.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((k) => (
                        <div key={k} className="col-12 border-bottom pb-2 mb-1">
                          <label className="text-muted small fw-semibold d-block mb-1">
                            {formatFieldLabel(k)}
                          </label>
                          <div><ValueRenderer value={parsedNew[k]} /></div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW MODE 3: RAW JSON */}
      {viewMode === "raw" && (
        <div className="row g-3">
          {hasOld && (
            <div className={hasNew ? "col-md-6" : "col-12"}>
              <div className="border rounded bg-light p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="text-danger small text-uppercase fw-bold m-0">
                    <i className="ti ti-code me-1"></i> Old Data (Raw JSON) {isFailed && "(FAILED)"}
                  </label>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary py-0 px-2"
                    style={{ fontSize: "12px" }}
                    onClick={() => copyToClipboard(JSON.stringify(parsedOld, null, 2), "Old Data JSON")}
                  >
                    <i className="ti ti-copy me-1"></i> Copy
                  </button>
                </div>
                <pre
                  className="bg-dark text-light p-3 rounded mb-0 font-monospace"
                  style={{ fontSize: "12px", maxHeight: "350px", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {JSON.stringify(parsedOld, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {hasNew && (
            <div className={hasOld ? "col-md-6" : "col-12"}>
              <div className="border rounded bg-light p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className={`small text-uppercase fw-bold m-0 ${isFailed ? "text-danger" : "text-success"}`}>
                    <i className="ti ti-code me-1"></i> New Data (Raw JSON) {isFailed && "(FAILED)"}
                  </label>
                  <button
                    type="button"
                    className="btn btn-xs btn-outline-secondary py-0 px-2"
                    style={{ fontSize: "12px" }}
                    onClick={() => copyToClipboard(JSON.stringify(parsedNew, null, 2), "New Data JSON")}
                  >
                    <i className="ti ti-copy me-1"></i> Copy
                  </button>
                </div>
                <pre
                  className="bg-dark text-light p-3 rounded mb-0 font-monospace"
                  style={{ fontSize: "12px", maxHeight: "350px", overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {JSON.stringify(parsedNew, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditDataViewer;
